/**
 * Single-source constants for the public marketing surfaces.
 *
 * Everything a human has to keep truthful lives here rather than inline in a
 * page, because the launch playbook's whole claims discipline depends on these
 * values being correctable in one edit: the founding-seat count has to be real
 * scarcity (WS2.4), the competitor prices carry a "verified" date that must be
 * re-checked the week each comparison page ships (WS4), and the Backblaze
 * region held an honest "United States" placeholder until someone read it off
 * the B2 console (WS1.4a) — a hopeful "Europe" would have turned the
 * subprocessor page into a false statement. That read happened on 2026-08-19.
 */

/** Canonical origin. Used to build canonical/OG/hreflang URLs and the sitemap. */
export const SITE_ORIGIN = 'https://studioflow.ch'

// ── swiss made software label (WS1.2) ────────────────────────────────────────
//
// The label is APPLIED FOR, not granted. Rendering the badge before the grant
// would be exactly the kind of unearned trust marker the claims pass exists to
// avoid, so both slots (footer + pricing section) stay dark behind this flag.
// Flip to `true` on the day the certificate arrives — nothing else changes.
export const SWISS_MADE_SOFTWARE_ENABLED = false

/** swissmadesoftware.org requires the badge to link back to them. */
export const SWISS_MADE_SOFTWARE_URL = 'https://www.swissmadesoftware.org/'

// ── Founding Studio program (WS2) ────────────────────────────────────────────
//
// Maintained by hand. The counter is only worth showing while it is TRUE — see
// FoundingSeatsCounter, which renders nothing once taken >= total, and the
// /founding route, which 404s the program section at the same point.
export const FOUNDING_SEATS_TOTAL = 10
export const FOUNDING_SEATS_TAKEN = 0

/** The program is over — every founding surface hides itself — once the cap is hit. */
export const FOUNDING_PROGRAM_OPEN = FOUNDING_SEATS_TAKEN < FOUNDING_SEATS_TOTAL

// ── Comparison pages (WS4) ───────────────────────────────────────────────────

/**
 * Month the competitor prices on the comparison pages were last read off the
 * competitor's own pricing page. Printed on every comparison page so a reader
 * can judge how stale the numbers are, and so re-verification is a visible,
 * datable act rather than a thing that quietly never happens.
 */
export const PRICES_VERIFIED = {
  en: 'August 2026',
  de: 'August 2026',
} as const

// ── Subprocessors (WS1.4a) ───────────────────────────────────────────────────

/**
 * Backblaze B2 bucket region. VERIFIED 2026-08-19: all three buckets
 * (`studioflow`, `studioflow-backups`, `studioflow-staging`) report the endpoint
 * `s3.eu-central-003.backblazeb2.com` in the B2 console — Backblaze's EU region,
 * Amsterdam, Netherlands. It stayed a single constant through the unverified
 * period precisely so this correction was one line and could not leave a stale
 * second copy behind.
 *
 * If a bucket is ever recreated, read the endpoint again before touching this:
 * a wrong region here is a false statement on a legal page.
 */
export const BACKBLAZE_B2_LOCATION = {
  en: 'Netherlands (EU)',
  de: 'Niederlande (EU)',
} as const

// ── Contact ──────────────────────────────────────────────────────────────────

/** Press alias — forwards to info@. Never a personal address (WS1.3). */
export const PRESS_EMAIL = 'press@studioflow.ch'
export const INFO_EMAIL = 'info@studioflow.ch'

/**
 * The founder's WhatsApp number, offered next to the email CTA on /founding.
 *
 * Unlike the aliases above this IS a personal line, and it ships on a public,
 * prerendered, crawlable page — a deliberate choice for the founding programme,
 * where "a direct line to the founder" is the actual product. It lives here so
 * that withdrawing it later is one edit rather than a hunt through JSX.
 *
 * `wa.me` takes the number in international format with no `+`, spaces or
 * dashes; anything else silently opens WhatsApp on an empty chat.
 */
export const FOUNDER_WHATSAPP_NUMBER = '+41772228811'
export const FOUNDER_WHATSAPP_URL = `https://wa.me/${FOUNDER_WHATSAPP_NUMBER.replace(/\D/g, '')}`
